import React, { useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import Animated, {
    useSharedValue,
    useAnimatedStyle,
    withRepeat,
    withTiming,
    withSequence,
} from 'react-native-reanimated';
import { theme } from '../constants/theme';

// Shimmer Animation Placeholder
const ShimmerPlaceholder = ({ width, height, borderRadius = 8, style = {} }: any) => {
    const opacity = useSharedValue(0.3);

    useEffect(() => {
        opacity.value = withRepeat(
            withSequence(
                withTiming(0.7, { duration: 1000 }),
                withTiming(0.3, { duration: 1000 })
            ),
            -1,
            false
        );
    }, []);

    const animatedStyle = useAnimatedStyle(() => {
        return {
            opacity: opacity.value,
        };
    });

    return (
        <View style={[{ width, height, borderRadius, overflow: 'hidden', backgroundColor: '#E1E9EE' }, style]}>
            <Animated.View
                style={[
                    StyleSheet.absoluteFillObject,
                    {
                        backgroundColor: '#F6F7F8',
                    },
                    animatedStyle,
                ]}
            />
        </View>
    );
};

// ============================================
// DASHBOARD SKELETON COMPONENTS
// ============================================

export const SkeletonCard = () => (
    <View style={styles.skeletonCard}>
        <View style={styles.skeletonRow}>
            <ShimmerPlaceholder width={40} height={40} borderRadius={20} />
            <View style={{ flex: 1, marginLeft: 12 }}>
                <ShimmerPlaceholder width="60%" height={16} borderRadius={4} style={{ marginBottom: 8 }} />
                <ShimmerPlaceholder width="40%" height={14} borderRadius={4} />
            </View>
            <ShimmerPlaceholder width={80} height={20} borderRadius={4} />
        </View>
        <View style={styles.skeletonDivider} />
        <View style={styles.skeletonRow}>
            <ShimmerPlaceholder width={40} height={40} borderRadius={20} />
            <View style={{ flex: 1, marginLeft: 12 }}>
                <ShimmerPlaceholder width="55%" height={16} borderRadius={4} style={{ marginBottom: 8 }} />
                <ShimmerPlaceholder width="35%" height={14} borderRadius={4} />
            </View>
            <ShimmerPlaceholder width={70} height={20} borderRadius={4} />
        </View>
    </View>
);

export const SkeletonOverview = () => (
    <View style={styles.overviewCard}>
        <View style={styles.overviewItem}>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                <ShimmerPlaceholder width={18} height={18} borderRadius={4} style={{ marginRight: 6 }} />
                <ShimmerPlaceholder width={80} height={12} borderRadius={4} />
            </View>
            <ShimmerPlaceholder width={60} height={32} borderRadius={6} />
        </View>
        <View style={styles.overviewDivider} />
        <View style={styles.overviewItem}>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                <ShimmerPlaceholder width={18} height={18} borderRadius={4} style={{ marginRight: 6 }} />
                <ShimmerPlaceholder width={75} height={12} borderRadius={4} />
            </View>
            <ShimmerPlaceholder width={50} height={32} borderRadius={6} />
        </View>
    </View>
);

export const SkeletonChart = () => {
    const barHeights = [45, 60, 38, 55, 70, 50, 65];
    return (
        <View style={styles.milkProductionCard}>
            <ShimmerPlaceholder width="65%" height={18} borderRadius={4} style={{ marginBottom: 12 }} />
            <View style={{ flexDirection: 'row', alignItems: 'baseline', marginBottom: 16 }}>
                <ShimmerPlaceholder width={80} height={28} borderRadius={6} style={{ marginRight: 8 }} />
                <ShimmerPlaceholder width={60} height={14} borderRadius={4} />
            </View>
            <View style={styles.chartContainer}>
                {barHeights.map((height, index) => (
                    <View key={index} style={styles.chartBarWrapper}>
                        <ShimmerPlaceholder width={10} height={10} borderRadius={4} style={{ marginBottom: 4 }} />
                        <View style={styles.chartBarContainer}>
                            <ShimmerPlaceholder width="80%" height={height} borderRadius={4} />
                        </View>
                        <ShimmerPlaceholder width={24} height={10} borderRadius={4} style={{ marginTop: 6 }} />
                    </View>
                ))}
            </View>
        </View>
    );
};

export const SkeletonExpenseCard = () => (
    <View style={styles.skeletonCard}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <ShimmerPlaceholder width="45%" height={18} borderRadius={4} />
            <ShimmerPlaceholder width={70} height={24} borderRadius={12} />
        </View>
        {[1, 2, 3].map((_, index) => (
            <View key={index}>
                <View style={styles.skeletonRow}>
                    <ShimmerPlaceholder width={36} height={36} borderRadius={8} />
                    <View style={{ flex: 1, marginLeft: 12 }}>
                        <ShimmerPlaceholder width="50%" height={16} borderRadius={4} />
                    </View>
                    <ShimmerPlaceholder width={90} height={18} borderRadius={4} />
                </View>
                {index < 2 && <View style={styles.skeletonDivider} />}
            </View>
        ))}
    </View>
);

export const SkeletonNetProfit = () => (
    <View style={[styles.skeletonCard, { borderWidth: 1, borderColor: '#E1E9EE' }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
            <ShimmerPlaceholder width={48} height={48} borderRadius={24} style={{ marginRight: 12 }} />
            <View style={{ flex: 1 }}>
                <ShimmerPlaceholder width="40%" height={18} borderRadius={4} style={{ marginBottom: 6 }} />
                <ShimmerPlaceholder width="30%" height={12} borderRadius={4} />
            </View>
            <ShimmerPlaceholder width={100} height={28} borderRadius={6} />
        </View>
        <View style={styles.skeletonDivider} />
        <View style={{ flexDirection: 'row', marginTop: 16 }}>
            <View style={{ flex: 1, alignItems: 'center' }}>
                <ShimmerPlaceholder width={60} height={12} borderRadius={4} style={{ marginBottom: 6 }} />
                <ShimmerPlaceholder width={80} height={16} borderRadius={4} />
            </View>
            <View style={{ width: 1, height: 30, backgroundColor: '#E1E9EE', marginHorizontal: 16 }} />
            <View style={{ flex: 1, alignItems: 'center' }}>
                <ShimmerPlaceholder width={60} height={12} borderRadius={4} style={{ marginBottom: 6 }} />
                <ShimmerPlaceholder width={80} height={16} borderRadius={4} />
            </View>
        </View>
    </View>
);

// ============================================
// COMMON LIST SKELETON COMPONENTS
// ============================================

// Simple List Item Skeleton (for cows, customers, etc.)
export const SkeletonListItem = () => (
    <View style={styles.listItemSkeleton}>
        <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
            <ShimmerPlaceholder width={48} height={48} borderRadius={8} style={{ marginRight: 12 }} />
            <View style={{ flex: 1 }}>
                <ShimmerPlaceholder width="70%" height={16} borderRadius={4} style={{ marginBottom: 8 }} />
                <ShimmerPlaceholder width="50%" height={12} borderRadius={4} />
            </View>
        </View>
        <ShimmerPlaceholder width={60} height={16} borderRadius={4} />
    </View>
);

// Simple List Skeleton with multiple items
export const SkeletonList = ({ itemCount = 5 }: { itemCount?: number }) => (
    <View style={styles.skeletonCard}>
        {Array.from({ length: itemCount }).map((_, index) => (
            <View key={index}>
                <SkeletonListItem />
                {index < itemCount - 1 && <View style={styles.skeletonDivider} />}
            </View>
        ))}
    </View>
);

// Compact List Item Skeleton (for activity logs, etc.)
export const SkeletonCompactListItem = () => (
    <View style={styles.compactListItemSkeleton}>
        <View style={{ flex: 1 }}>
            <ShimmerPlaceholder width="65%" height={14} borderRadius={4} style={{ marginBottom: 6 }} />
            <ShimmerPlaceholder width="45%" height={12} borderRadius={4} />
        </View>
        <ShimmerPlaceholder width={50} height={14} borderRadius={4} />
    </View>
);

// Compact List with multiple items
export const SkeletonCompactList = ({ itemCount = 8 }: { itemCount?: number }) => (
    <View style={styles.skeletonCard}>
        {Array.from({ length: itemCount }).map((_, index) => (
            <View key={index}>
                <SkeletonCompactListItem />
                {index < itemCount - 1 && <View style={styles.skeletonDivider} />}
            </View>
        ))}
    </View>
);

// Grid Item Skeleton (for statistics, etc.)
export const SkeletonGridItem = () => (
    <View style={styles.gridItemSkeleton}>
        <ShimmerPlaceholder width={32} height={32} borderRadius={8} style={{ marginBottom: 8, alignSelf: 'center' }} />
        <ShimmerPlaceholder width="80%" height={12} borderRadius={4} style={{ marginBottom: 8, alignSelf: 'center' }} />
        <ShimmerPlaceholder width="70%" height={16} borderRadius={4} style={{ alignSelf: 'center' }} />
    </View>
);

// Grid Skeleton with multiple items
export const SkeletonGrid = ({ itemCount = 4, columns = 2 }: { itemCount?: number; columns?: number }) => (
    <View style={styles.skeletonCard}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            {Array.from({ length: itemCount }).map((_, index) => (
                <View key={index} style={{ width: `${100 / columns - 2}%`, marginRight: columns > 1 ? '2%' : 0, marginBottom: 12 }}>
                    <SkeletonGridItem />
                </View>
            ))}
        </View>
    </View>
);

// Card Skeleton (for detailed item view)
export const SkeletonDetailCard = () => (
    <View style={styles.skeletonCard}>
        <ShimmerPlaceholder width="60%" height={18} borderRadius={4} style={{ marginBottom: 12 }} />
        <View style={{ gap: 8 }}>
            <ShimmerPlaceholder width="100%" height={14} borderRadius={4} />
            <ShimmerPlaceholder width="85%" height={14} borderRadius={4} />
        </View>
        <View style={styles.skeletonDivider} />
        <View style={{ gap: 8 }}>
            <ShimmerPlaceholder width="70%" height={12} borderRadius={4} />
            <ShimmerPlaceholder width="75%" height={12} borderRadius={4} />
        </View>
    </View>
);

// Button Skeleton
export const SkeletonButton = ({ width = '100%' }: { width?: string | number }) => (
    <ShimmerPlaceholder width={width} height={48} borderRadius={8} style={{ marginVertical: 8 }} />
);

const styles = StyleSheet.create({
    // Base Styles
    skeletonCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.md,
        ...theme.shadows.sm,
    },
    skeletonRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: theme.spacing.sm,
    },
    skeletonDivider: {
        height: 1,
        backgroundColor: '#E1E9EE',
        marginVertical: theme.spacing.sm,
    },

    // Overview Card
    overviewCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing.md,
        flexDirection: 'row',
        marginBottom: theme.spacing.md,
        ...theme.shadows.sm,
    },
    overviewItem: {
        flex: 1,
        alignItems: 'center',
    },
    overviewDivider: {
        width: 1,
        height: 50,
        backgroundColor: theme.colors.divider,
        marginHorizontal: theme.spacing.md,
    },

    // Chart Card
    milkProductionCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.md,
        ...theme.shadows.sm,
    },
    chartContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginTop: theme.spacing.lg,
        height: 120,
        paddingTop: theme.spacing.md,
    },
    chartBarWrapper: {
        flex: 1,
        alignItems: 'center',
        marginHorizontal: 2,
    },
    chartBarContainer: {
        flex: 1,
        width: '100%',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },

    // List Items
    listItemSkeleton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: theme.spacing.md,
    },
    compactListItemSkeleton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: theme.spacing.sm,
    },

    // Grid Item
    gridItemSkeleton: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#E1E9EE',
    },
});
